<template>
    <QDialog
        v-model="open"
        noBackdropDismiss
        @hide="closeModal"
    >
        <QCard class="flex column Card">
            <QBar class="bg-primary text-white">
                <div class="text-subtitle1 text-weight-medium">
                    {{ $t('visualization.filters.filterSets.title') }}
                </div>
                <QSpace />
                <QBtn
                    v-close-popup
                    dense
                    flat
                    color="white"
                    icon="close"
                    @click="closeModal"
                />
            </QBar>
            <q-card-section class="Body q-pt-none q-px-lg">
                <div class="q-mt-md">
                    <div class="Section">
                        <div class="TableButtons">
                            <div class="LeftButtons">
                                <QBtnDropdown
                                    autoClose
                                    noCaps
                                    color="primary"
                                    dense
                                    outline
                                    icon="filter_alt"
                                    menuAnchor="bottom end"
                                    :menuOffset="[65, 0]"
                                    :label="$t('visualization.filters.filterSets.config.filterSetsTable.load')"
                                    @click.stop
                                >
                                    <QList>
                                        <QItem
                                            clickable
                                            @click="addToActive"
                                        >
                                            <QItemSection>
                                                <QItemLabel>{{ $t('visualization.filters.filterSets.config.filterSetsTable.loadAsFilterSet') }}</QItemLabel>
                                            </QItemSection>
                                        </QItem>
                                        <QItem
                                            clickable
                                            @click="replaceFilterSets"
                                        >
                                            <QItemSection>
                                                <QItemLabel>{{ $t('visualization.filters.filterSets.config.filterSetsTable.loadAsFilters') }}</QItemLabel>
                                            </QItemSection>
                                        </QItem>
                                    </QList>
                                </QBtnDropdown>
                            </div>
                            <div class="RightButtons">
                                <QBtn
                                    icon="delete_outline"
                                    color="primary"
                                    noCaps
                                    dense
                                    outline
                                    :label="$t('visualization.filters.filterSets.config.filterSetsTable.removeSelected')"
                                    @click="removeFilterSets"
                                >
                                    <QTooltip>
                                        {{ $t('visualization.filters.filterSets.config.filterSetsTable.removeSelected') }}
                                    </QTooltip>
                                </QBtn>
                            </div>
                        </div>
                    </div>
                    <div class="TableContainer">
                        <QTable
                            ref="table"
                            v-model:selected="selected"
                            flat
                            outlined
                            bordered
                            virtualScroll
                            :rows="rows"
                            :columns="columns"
                            :loading="isLoading"
                            :rowsPerPageOptions="rowsPerPageOptions"
                            separator="none"
                            rowKey="uuid"
                            class="Table q-mt-sm"
                            tableHeaderClass="bg-grey-2"
                            selection="multiple"
                        >
                            <template #body-cell-name="props">
                                <QTd :props="props">
                                    <span>
                                        {{ props.row.name }}
                                    </span>
                                </QTd>
                            </template>
                            <template #body-cell-operator="props">
                                <QTd
                                    :props="props"
                                >
                                    <div class="OperatorCell">
                                        <QSelect
                                            v-model="props.row.operator"
                                            outlined
                                            class="Select"
                                            :options="operators"
                                            @update:modelValue="updateFilterSet(props.row)"
                                        />
                                    </div>
                                </QTd>
                            </template>
                            <template #body-cell-filters="props">
                                <QTd
                                    :props="props"
                                    class="FiltersTd"
                                >
                                    <div class="FiltersContainer t-0-5">
                                        <q-card
                                            v-for="(filter, index) in props.row.filters"
                                            :key="`${filter.uuid}-${index}`"
                                            flat
                                            bordered
                                            class="FilterContainer q-ml-xs"
                                        >
                                            <FilterIcon
                                                :id="getIconId(filter)"
                                                :height="30"
                                                :width="30"
                                            />
                                            <div class="FilterLabelContainer">
                                                <QTooltip
                                                    contentClass="bg-primary text-uppercase"
                                                    maxWidth="300px"
                                                >
                                                    {{ formatLabelValue(filter).tooltip }}
                                                </QTooltip>
                                                <WText
                                                    uppercase
                                                    :size="12"
                                                    :color="getColor(filter)"
                                                >
                                                    {{ formatLabelValue(filter).label }}
                                                </WText>
                                                <WText
                                                    :size="12"
                                                    weight="bold"
                                                    :color="getColor(filter)"
                                                >
                                                    {{ formatLabelValue(filter).value }}
                                                </WText>
                                            </div>
                                        </q-card>
                                    </div>
                                </QTd>
                            </template>
                        </QTable>
                    </div>
                </div>
            </q-card-section>
        </QCard>
        <ModalRequest
            v-model="confirmDeleteDialog"
            :title="$t('visualization.filters.filterSets.config.filterSetsTable.removeFiltersets')"
            :text="selection && selection.length > 1
                ? selection.length && $t('visualization.filters.filterSets.config.filterSetsTable.confirmationMultiple')
                : $t('visualization.filters.filterSets.config.filterSetsTable.confirmation')"
            :loading="isDeleting"
            @on-accept="confirmDeletion"
            @on-cancel="confirmDeleteDialog = false"
        />
    </QDialog>
</template>
<script>
import VueTypes from 'vue-types'
import {
    QBtnDropdown,
    QList,
    QItemSection,
    QItemLabel,
    QTooltip,
} from 'quasar'
import { ModalRequest } from '@/components'
import { filtersStorageMixin } from '@/mixins'
import {
    Api, apiRequest, notifyError, notifySuccess,
} from '@/api'
import { formatFilterForDisplay, getFilterIconId } from '@/utils/filters'
import {
    VARIANT_FILTERS, LOOP_FILTERS,
} from '@/screens/Project/Process/Filters/FilterModal/filtersEntity'
import FilterIcon from '../FilterModal/components/FilterIcon.vue'

const ROWS_PER_PAGE_OPTIONS = [
    5, 15, 30, 50, 75, 100,
]
export default {
    name: 'FilterSetsModal',
    components: {
        FilterIcon,
        QBtnDropdown,
        QList,
        QItemSection,
        QItemLabel,
        QTooltip,
        ModalRequest,
    },
    mixins: [filtersStorageMixin],
    props: {
        activeFilters: VueTypes.array,
        datasetId: VueTypes.string,
        isOpen: VueTypes.bool.def(false),
    },
    data() {
        return {
            operator: '',
            showCreate: false,
            isLoading: false,
            filterSetName: '',
            rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
            total: undefined,
            page: 1,
            rows: [],
            rowsPerPage: 15,
            selected: [],
            open: false,
            columns: [
                {
                    name: 'name', label: this.$t('visualization.filters.filterSets.config.filterSetsTable.filterName'), field: 'name', align: 'center',
                },
                {
                    name: 'operator', label: this.$t('visualization.filters.filterSets.config.filterSetsTable.operator'), field: 'operator', align: 'center',
                },
                {
                    name: 'filters', label: this.$t('visualization.filters.filterSets.config.filterSetsTable.filtersIncluded'), field: 'filters', align: 'center',
                },
            ],
            confirmDeleteDialog: false,
            variantNamesById: {},
            loopNamesById: {},
        }
    },
    computed: {
        operators() {
            return [
                {
                    label: this.$t('visualization.filters.filterSets.config.aggregationOperators.AND'),
                    value: 'AND',
                },
                {
                    label: this.$t('visualization.filters.filterSets.config.aggregationOperators.OR'),
                    value: 'OR',
                },
            ]
        },
    },
    watch: {
        isOpen(open) {
            this.open = open
            if (open) {
                this.getDatasetFilters()
            }
        },
    },
    methods: {
        ensureArray(value) {
            if (value === undefined || value === null) return []
            return Array.isArray(value) ? value : [value]
        },
        extractNameFromPayload(payload) {
            if (!payload) return undefined
            if (typeof payload === 'string') return payload
            if (typeof payload?.name === 'string') return payload.name
            if (typeof payload?.data?.name === 'string') return payload.data.name
            if (Array.isArray(payload?.data) && typeof payload.data?.[0]?.name === 'string') return payload.data[0].name
            if (Array.isArray(payload) && typeof payload?.[0]?.name === 'string') return payload[0].name
            return undefined
        },
        async fetchVariantName(variantId) {
            const key = variantId === undefined || variantId === null ? undefined : `${variantId}`
            if (!key) return undefined
            if (Object.prototype.hasOwnProperty.call(this.variantNamesById, key)) return this.variantNamesById[key]

            try {
                const payload = await apiRequest(Api().visualizations.variantNameOnly({ processId: this.datasetId, variantId: key }))
                const name = this.extractNameFromPayload(payload)
                this.variantNamesById[key] = name
                return name
            } catch (e) {
                this.variantNamesById[key] = undefined
                return undefined
            }
        },
        async fetchLoopName(loopId) {
            const key = loopId === undefined || loopId === null ? undefined : `${loopId}`
            if (!key) return undefined
            if (Object.prototype.hasOwnProperty.call(this.loopNamesById, key)) return this.loopNamesById[key]

            try {
                const payload = await apiRequest(Api().visualizations.loopNameOnly({ processId: this.datasetId, loopId: key }))
                const name = this.extractNameFromPayload(payload)
                this.loopNamesById[key] = name
                return name
            } catch (e) {
                this.loopNamesById[key] = undefined
                return undefined
            }
        },
        async enrichFilterSetsWithNames(filterSets = []) {
            const variantIds = new Set()
            const loopIds = new Set()

            const safeFilterSets = filterSets || []
            safeFilterSets.forEach((set) => {
                const safeFilters = (set && set.filters) ? set.filters : []
                safeFilters.forEach((filter) => {
                    if (!filter) return

                    if (VARIANT_FILTERS.includes(filter.id)) {
                        const ids = this.ensureArray(filter.values ?? filter.value ?? filter.key)
                        ids.filter(Boolean).forEach((id) => variantIds.add(`${id}`))
                    }
                    if (LOOP_FILTERS.includes(filter.id)) {
                        const ids = this.ensureArray(filter.values ?? filter.value ?? filter.key)
                        ids.filter(Boolean).forEach((id) => loopIds.add(`${id}`))
                    }
                })
            })

            await Promise.all(Array.from(variantIds).map((id) => this.fetchVariantName(id)))
            await Promise.all(Array.from(loopIds).map((id) => this.fetchLoopName(id)))

            const attachNames = (filter) => {
                if (!filter) return filter
                const next = { ...filter }

                if (VARIANT_FILTERS.includes(next.id)) {
                    const ids = this.ensureArray(next.values ?? next.value ?? next.key).map((id) => `${id}`)
                    const names = ids.map((id) => this.variantNamesById[id]).filter(Boolean)
                    if (names.length) {
                        next.meta = { ...(next.meta || {}), variantName: names }
                    }
                }

                if (LOOP_FILTERS.includes(next.id)) {
                    const ids = this.ensureArray(next.values ?? next.value ?? next.key).map((id) => `${id}`)
                    const names = ids.map((id) => this.loopNamesById[id]).filter(Boolean)
                    if (names.length) {
                        next.meta = { ...(next.meta || {}), loopName: names.length === 1 ? names[0] : names.join(', ') }
                    }
                }

                return next
            }

            return (filterSets || []).map((set) => ({
                ...set,
                filters: (set.filters || []).map(attachNames),
            }))
        },
        getDatasetFilters() {
            this.isLoading = true
            apiRequest(Api().datasets.getDatasetFilters({ datasetId: this.datasetId }))
                .then(
                    async ({ data }) => {
                        const enriched = await this.enrichFilterSetsWithNames(data || [])
                        this.rows = (enriched || []).map((set) => ({
                            name: set.name,
                            operator: set.operator,
                            filters: set.filters,
                            uuid: set.uuid,
                        }))
                    },
                )
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        updateFilterSet(filterSet) {
            const { datasetId } = this
            const filterSetId = filterSet.uuid
            const params = { operator: filterSet.operator.value }
            apiRequest(Api().datasets.updateFilterSetOperator({ datasetId, filterSetId, params }))
                .then(() => {
                    this.getDatasetFilters(datasetId)
                    const isActiveFilter = this.activeFilters.some((activeFilter) => activeFilter.uuid === filterSetId || activeFilter.filterset_uuid === filterSetId)
                    if (isActiveFilter) {
                        this.$emit('repeatCalls')
                    }
                })
                .catch(notifyError)
                .finally(() => { this.isLoading = false })
        },
        addToActive() {
            this.$emit('addToActive', this.selected)
            this.closeModal()
        },
        replaceFilterSets() {
            this.$emit('replaceFilters', this.selected)
            this.closeModal()
        },
        removeFilterSets() {
            if (!this.selected.length) return
            this.confirmDeleteDialog = true
        },
        confirmDeletion() {
            const uuids = this.selected.map((filterSet) => filterSet.uuid)
            const filtersSets = { filters: uuids }
            this.isLoading = true
            apiRequest(Api().datasets.deleteMultipleFiltersSets({ datasetId: this.datasetId, filtersSets }))
                .then(() => {
                    if (this.selected.length > 1) {
                        notifySuccess(this.$t('visualization.filters.filterSets.delete.successMultiple'))
                    } else {
                        notifySuccess(this.$t('visualization.filters.filterSets.delete.success'))
                    }
                    this.sendRemoveFilterSets(filtersSets)
                    this.getDatasetFilters()
                    this.selected = []
                })
                .catch(notifyError)
                .finally(() => {
                    this.isLoading = false
                    this.confirmDeleteDialog = false
                })
        },
        sendRemoveFilterSets(filterSets) {
            this.$emit('removedFilterSets', filterSets)
        },
        getIconId(filterSet) {
            return getFilterIconId(filterSet)
        },
        formatLabelValue(filter = {}) {
            if (VARIANT_FILTERS.includes(filter.id)) {
                this.$root.$emit('hasVariantFilter', true)
            }
            if (LOOP_FILTERS.includes(filter.id)) {
                this.$root.$emit('hasLoopFilter', true)
            }
            return formatFilterForDisplay(filter, { t: this.$t, variants: this.variants, durationFormat: 'd[D] h[h]:m[m]:s[s]' })
        },
        getColor({ pending, toDelete }) {
            if (toDelete) return 'red'
            if (pending) return 'gray03'
            return ''
        },
        closeModal() {
            this.$emit('close', false)
            this.resetModal()
        },
        resetModal() {
            this.selected = []
            this.filterSetName = ''
            this.showCreate = false
        },
    },
}
</script>
<style scoped lang="scss">
.Section {
  margin: 20px 0;
}
.MainTitle {
    font-size: x-large;
    font-weight: bold;
    margin-bottom: 20px;
    padding: 0 20px;
}
.Title {
    font-size: large;
    font-weight: bold;
}
.TableContainer {
    margin-bottom: 25px;
}
.FiltersTd {
    max-width: 530px;
}
.FiltersContainer {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    white-space: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    width: 100%;
    height: 100%;
}
.FilterContainer {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
}
.FilterLabelContainer {
    display: flex;
    flex-direction: column;
    padding-left: 10px;
    .WText {
        width: auto;
        max-width: 165px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
.BackArrow {
    right: 50px;
    position: absolute;
    top: 30px;
    color: $black;
    font-size: 24px;
    opacity: .65;
    cursor: pointer;
}
.Select {
    width: fit-content;
    min-width: 90px;
}
.OperatorCell {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
}
.TableButtons {
    display: flex;
}
.LeftButtons {
    width: 50%;
    display: flex;
    gap: 20px;
}
.RightButtons {
    width: 50%;
    display: flex;
    justify-content: flex-end;
}
.ApplyButton {
    width: 10%;
    margin-left: 20px;
}

.header-text {
    margin-bottom: 1.5rem;

    @media (min-width: 768px) {
        margin-bottom: 1.5rem;
    }
}
.q-dialog__inner--minimized > div {
    min-width: 70%;
    min-height: 70%;
}
.Card {
    overflow:hidden;
    flex-wrap:nowrap;
}
.Header {
    position:sticky;
    padding-bottom:0px;
}
.Body {
    overflow-y:auto;
}
.Confirmation {
    min-width: 300px !important;
    min-height: 0px !important;
}
</style>
